import React, { useState } from 'react';
import { Button, Popconfirm, Table, Space, Tooltip, Grid, Spin, Empty, Pagination } from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    FileTextOutlined,
    WarningOutlined,
    HistoryOutlined
} from '@ant-design/icons';
import moment from 'moment';
import EquipmentFormModal from './EquipFormModal';
import EquipmentDocsModal from './EquipDocModal';
import MaintenanceModal from './MaintenanceModal';
import IssuesModal from './IssuesModal';
import {
    useGetEquipments,
    useAddEquipment,
    useEditEquipment,
    useDeleteEquipment
} from '../../../hooks/useEquipment';

const { useBreakpoint } = Grid;

const PAGE_SIZE = 10;
const formatDate = (date) => (date ? moment(date).format('DD/MM/YYYY') : '-');

const EquipmentsScreen = () => {
    const screens = useBreakpoint();
    const isMobile = screens.md === false; // < 768px

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDocsModalVisible, setIsDocsModalVisible] = useState(false);
    const [isMaintenanceModalVisible, setIsMaintenanceModalVisible] = useState(false);
    const [isIssuesModalVisible, setIsIssuesModalVisible] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState(null);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [mobilePage, setMobilePage] = useState(1);

    // Queries and Mutations
    const { data: equipments = [], isLoading } = useGetEquipments();
    const addEquipmentMutation = useAddEquipment();
    const editEquipmentMutation = useEditEquipment();
    const deleteEquipmentMutation = useDeleteEquipment();

    const handleAdd = () => {
        setEditingEquipment(null);
        setIsModalVisible(true);
    };

    const handleEdit = (equipment) => {
        setEditingEquipment(equipment);
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        deleteEquipmentMutation.mutate(id);
    };

    const handleViewDocs = (equipment) => {
        setSelectedEquipment(equipment);
        setIsDocsModalVisible(true);
    };

    const handleViewMaintenance = (equipment) => {
        setSelectedEquipment(equipment);
        setIsMaintenanceModalVisible(true);
    };

    const handleViewIssues = (equipment) => {
        setSelectedEquipment(equipment);
        setIsIssuesModalVisible(true);
    };

    const handleSubmit = async (values) => {
        if (editingEquipment) {
            editEquipmentMutation.mutate(
                { id: editingEquipment.id, values },
                {
                    onSuccess: () => setIsModalVisible(false)
                }
            );
        } else {
            addEquipmentMutation.mutate(values, {
                onSuccess: () => setIsModalVisible(false)
            });
        }
    };

    // ─── Desktop / tablet columns ───────────────────────────────────────────────
    const columns = [
        {
            title: '#',
            dataIndex: 'code',
            key: 'code',
            width: 60,
            sorter: (a, b) => a.code.localeCompare(b.code),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 120,
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Supplier',
            dataIndex: 'supplier',
            key: 'supplier',
            width: 140,
        },
        {
            title: 'Maintenance Company',
            dataIndex: 'maintenanceCompany',
            key: 'maintenanceCompany',
            width: 160,
        },
        {
            title: 'Receive Date',
            dataIndex: 'reciveDate',
            key: 'reciveDate',
            width: 110,
            render: formatDate,
        },
        {
            title: 'Last Maintenance',
            dataIndex: 'lastMaintenanceDate',
            key: 'lastMaintenanceDate',
            width: 130,
            render: formatDate,
        },
        {
            title: 'Next Maintenance',
            dataIndex: 'mintanaceDate',
            key: 'mintanaceDate',
            width: 130,
            render: (date) => <Space>{formatDate(date)}</Space>,
        },
        {
            title: 'Reminder (Hours)',
            dataIndex: 'maintenanceReminderHours',
            key: 'maintenanceReminderHours',
            width: 110,
        },
        {
            title: 'Files',
            key: 'docs',
            width: 90,
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<FileTextOutlined />}
                    onClick={() => handleViewDocs(record)}
                >
                    {record.equipmentDocs?.length || 0}
                </Button>
            ),
        },
        {
            title: 'History',
            key: 'maintenance',
            width: 110,
            render: (_, record) => (
                <Tooltip title="View Maintenance History">
                    <Button
                        type="link"
                        icon={<HistoryOutlined />}
                        onClick={() => handleViewMaintenance(record)}
                        className="text-primary hover:text-primary_hover"
                    />
                </Tooltip>
            ),
        },
        {
            title: 'Report Issue',
            key: 'issues',
            width: 100,
            render: (_, record) => (
                <Tooltip title="View/Report Issues">
                    <Button
                        type="link"
                        icon={<WarningOutlined />}
                        onClick={() => handleViewIssues(record)}
                        className="text-orange-600 hover:text-orange-800"
                    >
                        {record.totalIssues}
                    </Button>
                </Tooltip>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        type="text"
                        className="text-blue-600 hover:text-blue-800"
                    />
                    <Popconfirm
                        title="Are you sure to delete this equipment?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            type="text"
                            loading={deleteEquipmentMutation.isLoading}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // ─── Mobile cards ───────────────────────────────────────────────────────────
    const InfoItem = ({ label, value }) => (
        <div className="min-w-0">
            <div className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</div>
            <div className="text-sm text-gray-800 font-medium break-words">{value || '-'}</div>
        </div>
    );

    const pagedEquipments = equipments.slice((mobilePage - 1) * PAGE_SIZE, mobilePage * PAGE_SIZE);

    const renderMobileCards = () => (
        <Spin spinning={isLoading}>
            {!isLoading && equipments.length === 0 ? (
                <Empty className="py-10" />
            ) : (
                <div className="flex flex-col gap-3 min-h-[120px]">
                    {pagedEquipments.map((record) => (
                        <div
                            key={record.id}
                            className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                        >
                            <div className="h-1 bg-gradient-to-r from-primary to-primary/50" />
                            <div className="p-3">
                                {/* Header: name + code */}
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-bold text-primary text-base leading-tight break-words min-w-0">
                                        {record.name}
                                    </h3>
                                    <span className="shrink-0 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                                        #{record.code}
                                    </span>
                                </div>

                                {/* Info grid */}
                                <div className="grid grid-cols-2 gap-x-3 gap-y-3 mt-3">
                                    <InfoItem label="Supplier" value={record.supplier} />
                                    <InfoItem label="Maintenance Co." value={record.maintenanceCompany} />
                                    <InfoItem label="Receive Date" value={formatDate(record.reciveDate)} />
                                    <InfoItem label="Reminder (Hours)" value={record.maintenanceReminderHours} />
                                    <InfoItem label="Last Maintenance" value={formatDate(record.lastMaintenanceDate)} />
                                    <InfoItem label="Next Maintenance" value={formatDate(record.mintanaceDate)} />
                                </div>

                                {/* Quick links: files / history / issues */}
                                <div className="grid grid-cols-3 gap-2 mt-4">
                                    <button
                                        onClick={() => handleViewDocs(record)}
                                        className="flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg bg-blue-50 text-blue-600 active:scale-95 transition-transform"
                                    >
                                        <FileTextOutlined />
                                        <span className="text-[11px] font-semibold">
                                            Files ({record.equipmentDocs?.length || 0})
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => handleViewMaintenance(record)}
                                        className="flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg bg-primary/10 text-primary active:scale-95 transition-transform"
                                    >
                                        <HistoryOutlined />
                                        <span className="text-[11px] font-semibold">History</span>
                                    </button>
                                    <button
                                        onClick={() => handleViewIssues(record)}
                                        className="flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg bg-orange-50 text-orange-600 active:scale-95 transition-transform"
                                    >
                                        <WarningOutlined />
                                        <span className="text-[11px] font-semibold">
                                            Issues ({record.totalIssues ?? 0})
                                        </span>
                                    </button>
                                </div>

                                {/* Edit / Delete */}
                                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                                    <Button
                                        icon={<EditOutlined />}
                                        className="flex-1 text-primary border-primary"
                                        onClick={() => handleEdit(record)}
                                    >
                                        Edit
                                    </Button>
                                    <Popconfirm
                                        title="Are you sure to delete this equipment?"
                                        placement="topRight"
                                        overlayStyle={{ maxWidth: 'calc(100vw - 24px)' }}
                                        onConfirm={() => handleDelete(record.id)}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            loading={deleteEquipmentMutation.isLoading}
                                        />
                                    </Popconfirm>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {equipments.length > PAGE_SIZE && (
                <div className="flex justify-center mt-4">
                    <Pagination
                        simple
                        current={mobilePage}
                        pageSize={PAGE_SIZE}
                        total={equipments.length}
                        showSizeChanger={false}
                        onChange={(page) => {
                            setMobilePage(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                    />
                </div>
            )}
        </Spin>
    );

    return (
        <div className="p-3 sm:p-4 md:p-6 overflow-x-hidden">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className="text-lg md:text-xl font-semibold">Equipments Management</h2>
                <Button
                    type="default"
                    size={isMobile ? 'middle' : 'large'}
                    block={isMobile}
                    className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover md:hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                >
                    Add Equipment
                </Button>
            </div>

            {isMobile ? (
                renderMobileCards()
            ) : (
                <Table
                    columns={columns}
                    dataSource={equipments}
                    loading={isLoading}
                    rowKey="id"
                    scroll={{ x: 1350 }}
                    pagination={{
                        pageSize: PAGE_SIZE,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} equipments`,
                    }}
                    className="shadow-md rounded-lg"
                />
            )}

            <EquipmentFormModal
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onSubmit={handleSubmit}
                editingEquipment={editingEquipment}
                isLoading={addEquipmentMutation.isLoading || editEquipmentMutation.isLoading}
            />

            <EquipmentDocsModal
                visible={isDocsModalVisible}
                onCancel={() => setIsDocsModalVisible(false)}
                equipment={equipments.find(eq => eq.id === selectedEquipment?.id) || selectedEquipment}
            />

            <MaintenanceModal
                visible={isMaintenanceModalVisible}
                onCancel={() => setIsMaintenanceModalVisible(false)}
                equipment={selectedEquipment}
            />

            <IssuesModal
                visible={isIssuesModalVisible}
                onCancel={() => setIsIssuesModalVisible(false)}
                equipment={selectedEquipment}
            />
        </div>
    );
};

export default EquipmentsScreen;